import React, { useState, useEffect } from 'react';
import { 
  Settings,
  Server,
  Database,
  Shield,
  Mail,
  Globe,
  Clock,
  Cloud,
  Key,
  File,
  AlertTriangle,
  Edit,
  Save,
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
  HelpCircle,
  RefreshCw,
  Loader,
  AlertCircle
} from 'lucide-react';

// Composants UI de base
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Import dynamique de tous les fichiers JSON du répertoire config
const configContext = require.context('../data/config', false, /\.json$/);

// Mapping des icônes
const ICONS_MAP = {
  Settings, Server, Database, Shield, Mail, Globe, Clock, Cloud, Key, File
};

// Fonction pour obtenir l'icône appropriée
const getIcon = (iconName, fallback = Settings) => {
  return ICONS_MAP[iconName] || fallback;
};

// Helper pour détecter le type de configuration depuis les métadonnées ou le nom de fichier
const detectConfigurationType = (data, filename) => {
  const title = data.metadata?.title?.toLowerCase() || '';
  const filenameLC = filename.toLowerCase();
  
  if (filenameLC.includes('system') || title.includes('système')) {
    return { icon: Server, name: 'Configuration Système', description: 'Paramètres système et serveur' };
  }
  if (filenameLC.includes('security') || title.includes('sécurité')) {
    return { icon: Shield, name: 'Configuration Sécurité', description: 'Paramètres de sécurité et authentification' };
  }
  if (filenameLC.includes('integration') || title.includes('intégration')) {
    return { icon: Globe, name: 'Configuration Intégrations', description: 'Paramètres des services externes' };
  }
  if (filenameLC.includes('database') || title.includes('base de données')) {
    return { icon: Database, name: 'Configuration Base de Données', description: 'Paramètres de base de données' };
  }
  
  // Type par défaut
  return { icon: Settings, name: title || 'Configuration', description: 'Paramètres de configuration' };
};

// Validation de structure d'un fichier de configuration
const validateConfigurationStructure = (data, filename) => {
  const errors = [];
  
  // Vérifier les champs obligatoires
  if (!data.metadata) {
    errors.push('Métadonnées manquantes');
  }
  
  if (!data.categories || !Array.isArray(data.categories)) {
    errors.push('Catégories manquantes ou invalides');
  }
  
  // Vérifier que chaque catégorie a des configurations
  if (data.categories) {
    data.categories.forEach((category, index) => {
      if (!category.id) errors.push(`Catégorie ${index}: id manquant`);
      if (!category.name) errors.push(`Catégorie ${index}: nom manquant`);
      if (!category.configurations || !Array.isArray(category.configurations)) {
        errors.push(`Catégorie ${category.name || index}: configurations manquantes`);
      }
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings: []
  };
};

// Extraire les métadonnées d'un fichier de configuration
const extractConfigurationMetadata = (data, filename) => {
  const typeInfo = detectConfigurationType(data, filename);
  
  return {
    filename,
    name: typeInfo.name,
    icon: typeInfo.icon,
    description: data.metadata?.description || typeInfo.description,
    version: data.metadata?.version || '1.0.0',
    lastUpdate: data.metadata?.lastUpdate || new Date().toISOString(),
    categoriesCount: data.categories?.length || 0,
    parametersCount: data.categories?.reduce((total, cat) => 
      total + (cat.configurations?.reduce((catTotal, config) => 
        catTotal + (config.parameters?.length || 0), 0) || 0), 0) || 0
  };
};

// Composant de paramètre de configuration
const ParameterDetail = ({ parameter, isEditing }) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(parameter.key);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-start space-x-3">
          <Key className="h-5 w-5 text-blue-500 mt-1" />
          <div>
            <div className="flex items-center space-x-2">
              <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {parameter.key}
              </code>
              <button
                onClick={copyToClipboard}
                className="p-1 hover:bg-gray-200 rounded"
              >
                {isCopied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">{parameter.description}</p>
          </div>
        </div>
        {parameter.required && (
          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
            Requis
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="font-medium text-gray-700">Type:</span>
          <span className="ml-2">{parameter.type}</span>
        </div>
        {parameter.defaultValue !== null && parameter.defaultValue !== undefined && (
          <div>
            <span className="font-medium text-gray-700">Défaut:</span>
            <code className="ml-2 bg-gray-100 px-2 py-1 rounded">
              {String(parameter.defaultValue)}
            </code>
          </div>
        )}
        {parameter.validation && (
          <div>
            <span className="font-medium text-gray-700">Validation:</span>
            <span className="ml-2">{parameter.validation}</span>
          </div>
        )}
        {parameter.example && (
          <div>
            <span className="font-medium text-gray-700">Exemple:</span>
            <code className="ml-2 bg-gray-100 px-2 py-1 rounded">
              {parameter.example}
            </code>
          </div>
        )}
      </div>

      {(parameter.impact || parameter.security) && (
        <div className="mt-4 flex flex-wrap gap-4">
          {parameter.impact && (
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <span className="text-sm text-gray-600">{parameter.impact}</span>
            </div>
          )}
          {parameter.security && (
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-red-500" />
              <span className="text-sm text-gray-600">{parameter.security}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Composant de documentation de configuration
const ConfigurationDetail = ({ config }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <Card className="mb-6">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50"
        onClick={handleToggle}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            {config.icon && (
              <div className="p-2 bg-blue-50 rounded-lg">
                <Settings className="h-5 w-5 text-blue-500" />
              </div>
            )}
            <div>
              <CardTitle>{config.name}</CardTitle>
              <p className="text-sm text-gray-600">{config.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm bg-gray-100 text-gray-700 px-2 py-1 rounded">
              {config.environment}
            </span>
            <button 
              className="p-2 hover:bg-gray-100 rounded-full transition-colors" 
              onClick={handleToggle}
            >
              {isExpanded ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-6">
            {/* Section des paramètres */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Paramètres de Configuration
              </h4>
              <div className="space-y-4">
                {config.parameters && config.parameters.map((parameter) => (
                  <ParameterDetail
                    key={parameter.key}
                    parameter={parameter}
                  />
                ))}
              </div>
            </div>

            {/* Section de documentation */}
            {config.documentation && (
              <div className="pt-6 border-t">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  Guide d'Implémentation
                </h4>
                
                <div className="space-y-4">
                  {/* Section configuration */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">
                      Configuration
                    </h5>
                    <p className="text-sm text-gray-600">
                      {config.documentation.setup}
                    </p>
                  </div>

                  {/* Section avertissements */}
                  {config.documentation.warnings && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        Points d'Attention
                      </h5>
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <div className="space-y-2">
                          {config.documentation.warnings.map((warning, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />
                              <span className="text-sm text-gray-600">{warning}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section bonnes pratiques */}
                  {config.documentation.bestPractices && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        Bonnes Pratiques
                      </h5>
                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="space-y-2">
                          {config.documentation.bestPractices.map((practice, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <Check className="h-4 w-4 text-green-500 mt-0.5" />
                              <span className="text-sm text-gray-600">{practice}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Composant de catégorie de configuration
const CategorySection = ({ category }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const IconComponent = getIcon(category.icon, Settings);

  return (
    <div className="mb-8">
      <div 
        className="flex items-center space-x-4 mb-6 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="p-3 bg-blue-50 rounded-lg">
          <IconComponent className="h-6 w-6 text-blue-500" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{category.name}</h2>
          <p className="text-gray-600">{category.description}</p>
        </div>
        <button className="ml-auto p-2 hover:bg-gray-100 rounded-full">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-6 pl-14">
          {category.configurations && category.configurations.map((config) => (
            <ConfigurationDetail
              key={config.id}
              config={config}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Composant principal de documentation de configuration
const DocumentConfiguration = () => {
  const [configurations, setConfigurations] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Charger les fichiers de configuration au montage
  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Démarrage du chargement des configurations...');
      
      const keys = configContext.keys();
      const loadedConfigurations = [];
      const failedFiles = [];
      
      // Parcourir tous les fichiers trouvés
      keys.forEach(key => {
        const filename = key.replace('./', '');
        
        try {
          // Charger le fichier
          const data = configContext(key);
          
          // Valider la structure
          const validation = validateConfigurationStructure(data, filename);
          
          if (validation.isValid) {
            // Extraire les métadonnées
            const metadata = extractConfigurationMetadata(data, filename);
            
            loadedConfigurations.push({
              ...metadata,
              data
            });
          } else {
            failedFiles.push({
              filename,
              errors: validation.errors
            });
          }
        } catch (err) {
          failedFiles.push({
            filename,
            error: err.message
          });
          console.error(`Erreur lors du chargement de ${filename}:`, err);
        }
      });
      
      console.log(`📁 ${loadedConfigurations.length} configurations chargées`);
      
      // Trier par nom
      loadedConfigurations.sort((a, b) => a.name.localeCompare(b.name));
      
      setConfigurations(loadedConfigurations);
      
      // Sélectionner la première configuration par défaut
      if (loadedConfigurations.length > 0) {
        setSelectedConfig(loadedConfigurations[0]);
        console.log(`✅ Première configuration sélectionnée: ${loadedConfigurations[0].name}`);
      }
      
      // Log des fichiers avec erreurs (pour debug)
      if (failedFiles.length > 0) {
        console.log('⚠️ Fichiers avec erreurs:', failedFiles);
      }
    } catch (err) {
      setError('Erreur lors du chargement des configurations');
      console.error('Error loading configurations:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les catégories selon la recherche
  const getFilteredCategories = () => {
    if (!selectedConfig || !selectedConfig.data) return [];
    
    return selectedConfig.data.categories.filter(category =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.configurations && category.configurations.some(config =>
        config.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (config.parameters && config.parameters.some(param =>
          param.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
          param.description.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      ))
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <Loader className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">Chargement des configurations...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent>
              <div className="flex items-center space-x-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
              <Button
                onClick={loadConfigurations}
                className="mt-4"
                variant="outline"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const filteredCategories = getFilteredCategories();

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Sélection de fichier de configuration */}
      <div className="mb-6">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {configurations.map((config) => {
            const Icon = config.icon;
            return (
              <button
                key={config.filename}
                onClick={() => setSelectedConfig(config)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center space-x-2 ${
                  selectedConfig?.filename === config.filename
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{config.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {selectedConfig && (
        <>
          {/* En-tête */}
          <div className="mb-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {selectedConfig.data.metadata.title}
                </h1>
                <p className="mt-1 text-gray-600">
                  Version {selectedConfig.data.metadata.version} - 
                  Dernière mise à jour : {selectedConfig.data.metadata.lastUpdate}
                </p>
                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                  <span>{selectedConfig.categoriesCount} catégories</span>
                  <span>•</span>
                  <span>{selectedConfig.parametersCount} paramètres</span>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setIsEditing(!isEditing)}
                className="flex items-center space-x-2"
              >
                {isEditing ? (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Sauvegarder</span>
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4" />
                    <span>Modifier</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Barre de recherche */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une configuration..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Guide rapide */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <HelpCircle className="h-5 w-5 text-blue-500" />
                <span>Guide d'Utilisation</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">
                  Cette documentation détaille toutes les options de configuration disponibles dans le système. 
                  Chaque configuration est organisée par catégorie et contient des informations détaillées sur :
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center space-x-2">
                    <Key className="h-4 w-4 text-blue-500" />
                    <span>Les paramètres et leurs valeurs acceptées</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    <span>Les points d'attention et impacts potentiels</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span>Les bonnes pratiques recommandées</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Catégories de configuration */}
          <div className="space-y-8">
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => (
                <CategorySection 
                  key={category.id} 
                  category={category} 
                />
              ))
            ) : (
              <Card>
                <CardContent>
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      Aucune configuration trouvée pour "{searchTerm}"
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Modal d'ajout de configuration */}
          {isEditing && (
            <div className="fixed bottom-4 right-4">
              <Button
                onClick={() => {
                  console.log('Add new configuration');
                }}
                className="flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Ajouter une configuration</span>
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentConfiguration;